/**
 * KartUygunsuzluklari.jsx — "Giriş kartı neden çıkmıyor?" listesi (backlog #278)
 *
 * İki yerde aynı liste: Personel Detayı'nın "Giriş Kartı Durumu" kartı ve Kişi Listesi'ndeki Kart Yazdır
 * penceresi (Components/Kisi/KartYazdirModal). Kural backend'de (`uygunsuzluklar[]`, kod sözlüğü iki yerde
 * aynı); burası yalnızca kod → etiket ve kod → "nerede düzeltilir" bağlantısı.
 *
 * Bağlantı hedefleri (kişi sayfası: /Management/Kisi/Personeller/{personId}?fuarId=, #365: firma bağlamı yok):
 *   KVKK ve KIMLIK_DOGRULAMA → ?tab=kimlik&vurgu=kvkk|kimlik (sekme adı "Kontroller", #284/#368; ilgili kart çerçevelenir, #481) ·
 *   BELGE:<anahtar> → ?tab=belgeler&belge=<anahtar> (BELGE:meslekiEgitim → ?tab=belgeler&alt=meslek, yüklemesi orada, #363)
 *   FIRMA_BELGELERI / FIRMA_GENEL_BELGELERI / vergi numarası → /Management/Firmalar/Profil/{firmaId}?tab=belgeler
 *   YASAKLI, FIRMA_REDDETTI → yalnızca metin (düzeltilecek bir yer yok).
 * Her bağlantı O GİRDİNİN `firmaId`'sini kullanır — bir kişi birden çok firmada görevli olabilir.
 *
 * Props:
 *   uygunsuzluklar  string[] (backend kodları)
 *   vergiNoYok      true → listeye "Firmanın vergi numarası yok" eklenir (yalnızca kart-karari bilir)
 *   firmaGrubu      kişinin firmasının FIRMA belge grubu (`belgeGruplari[]` / `firmaBelgeGruplari[]` içinden `kod: FIRMA`);
 *                   FIRMA_GENEL_BELGELERI metni kaç belgenin yeterli olduğunu (`kartIcinGerekli`) ondan alır
 *   yeniSekme       true → bağlantılar yeni sekmede (popup: çalışılan sayfa kaybolmasın)
 *   onSayfaIci      ({ tab, alt, belge, vurgu }) => void; verilirse kişi sayfasının kendi sekmelerine giden hedefler
 *                   bağlantı yerine bunu çağırır (Personel Detayı: aynı sayfada sekme değiştir ve hedefi çerçevele, #481)
 *   kisi            (#350) kişi nesnesi (`firmalar[].kisi`, `documents{}` taşır): belge engelleri "SGK: Onay bekliyor" gibi
 *                   durumlarıyla yazılır. Verilmezse eski "Eksik/onaysız belge: SGK" metni.
 *   geri, geriEtiket (#353) yeni sekmede açılan Kişi sayfası bağlantılarına `geri` / `geriEtiket` eklenir: kişi
 *                   sayfasındaki "geri dön" bağlantısı, çağıran sayfanın tam adresine (süzgeç/sayfa dahil) döner.
 *   firmaSecenekleri (#400) birden çok firmanın evrakı eksikken "Firma Profili" bağlantısı bir açılır liste olur ve
 *                   hangi firmanın profiline gidileceğini sorar (kartBirlestir.js); tek firmada doğrudan bağlantı.
 *
 * Satırlar kişiye aittir, firmaya değil (#400): "SGK: Eksik" firma adı taşımaz. Hangi firmanın hangi engeli taşıdığını
 * ayıklamak çağıranın işidir (kartBirlestir.js) — bu bileşen verilen kodları olduğu gibi yazar.
 *
 * Satır düzeni (#350): küçük renkli ikon karesi (Kişi Listesi'ndeki belge kareleriyle aynı dil) · metin · düzeltme bağlantısı.
 */
import React from "react";
import { Link } from "react-router-dom";
import { UncontrolledDropdown, DropdownToggle, DropdownMenu, DropdownItem } from "reactstrap";
import {
  ArrowUpRight, Ban, Building2, CheckCircle2, ChevronDown, ClipboardCheck, FileText, IdCard, Info, Receipt, ShieldCheck,
} from "lucide-react";
import { kisiBelgeLabel } from "../../services/kisiAdminService";
import { STATUS_HEX, NEUTRAL_GRAY } from "../Projects/StandPills";
import { PERSONEL_BELGE_META } from "./PersonelKartParcalari";
import { firmaAdi } from "./PersonelFirmaBelgeParcalari";
import { kisiSayfasiUrl } from "./kisiBaglantilari";
import { meslekBilgiEksikleri } from "./meslekler";
import { useTranslation } from "react-i18next";
import i18n from "../../i18n";
import { donemYaz } from "../../services/personelBelgeService";

/**
 * `uygunsuzluklar[]` kodlarını ekrana. `BELGE:<key>` dinamik olduğu için ayrı ele alınır.
 * `firmaGrubu`: kişinin firmasının FIRMA belge grubu (varsa); işveren yolunun kaç belge istediğini
 * (`kartIcinGerekli`, backend #276) ondan okur, yoksa 1.
 */
export function uygunsuzlukLabel(code, firmaGrubu = null) {
  if (code === "YASAKLI") return i18n.t("components:kartUygunsuzluklari.kisininFuarAlaninaGirisiYasakli");
  if (code === "FIRMA_REDDETTI") return i18n.t("components:kartUygunsuzluklari.firmaBuKisininEslemesiniReddetti");
  if (code === "KVKK") return i18n.t("components:kartUygunsuzluklari.kvkkOnayiYokOnayliBelge");
  if (code === "FIRMA_BELGELERI") return i18n.t("components:kartUygunsuzluklari.firmaninUcIsgBelgesiOnayli");
  // İşveren yolunun firma şartı (#125, #294) — İSG grubu değil FIRMA grubu; üçü de değil, `kartIcinGerekli` tanesi.
  if (code === "FIRMA_GENEL_BELGELERI") {
    return i18n.t("components:kartUygunsuzluklari.firmaBelgeleriNdenVergiLevhasi", { v: firmaGrubu?.kartIcinGerekli ?? 1 });
  }
  if (code === "KIMLIK_DOGRULAMA") return i18n.t("components:kartUygunsuzluklari.kimlikDogrulamasiYapilmamis");
  if (code === "VERGI_NO") return i18n.t("components:kartUygunsuzluklari.firmaninVergiNumarasiYok");
  if (code?.startsWith("BELGE:")) return i18n.t("components:kartUygunsuzluklari.eksikOnaysizBelge", { kisiBelgeLabel: kisiBelgeLabel(code.slice(6)) });
  return code;
}

// WAS (#349: the month name follows the UI language now):
// const AY_ADLARI = ["Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran", "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık"];

// WAS (until #268): the month formatter lived here as a file-local `donemYaz`; it is shared now (personelBelgeService.donemYaz), same output.

/**
 * Bir kişi satırının `uyarilar[]` kodlarını (#267) metne çevirir. Uyarı ASLA bloklamaz; kartı basan kişi
 * e-Devlet'e bakıp bakmayacağına kendisi karar verir. Boşsa [].
 */
export function kartUyarilari(kisi) {
  const sgk = kisi?.documents?.sgk;
  return (kisi?.uyarilar || [])
    .map((u) => (typeof u === "string" ? u : u?.kod))
    .filter(Boolean)
    .map((kod) => {
      if (kod === "SGK_HIZMET_DOKUMU_ESKI") {
        return i18n.t("components:kartUygunsuzluklari.sgkHizmetDokumuAyEski", { donemYaz: donemYaz(sgk?.belgeDonemi), belgeDonemiAyFarki: sgk?.belgeDonemiAyFarki });
      }
      if (kod === "SGK_HIZMET_DOKUMU_DONEM_YOK") return i18n.t("components:kartUygunsuzluklari.sgkHizmetDokumuDonemiGirilmemis");
      // #373/#374: KKD kartı hiç engellemez; eksik kalemin adı satırdaki `kkd.kalemler`den okunur (engelleyen = seviye 2, uyaran = 1).
      if (kod === "KKD_EKSIK" || kod === "KKD_UYARI") {
        const seviye = kod === "KKD_EKSIK" ? 2 : 1;
        const adlar = (kisi?.kkd?.kalemler || []).filter((k) => k.seviye === seviye && !k.mevcut).map((k) => k.ad);
        return i18n.t("components:kartUygunsuzluklari.kkdKartUygunlugunuEtkilemez", { v: kod === "KKD_EKSIK" ? "eksik" : i18n.t("components:kartUygunsuzluklari.uyarisi"), v2: adlar.length ? `: ${adlar.join(", ")}` : "" });
      }
      return kod;
    });
}

/** Kod → { etiket, tab?, alt?, belge?, href, secenekler? } ya da null (düzeltilecek yer yok). */
function hedefOf(code, { firmaId, personId, fuarId, geri, geriEtiket, firmaSecenekleri }) {
  // #365: kişi sayfasında firma anahtarı kalmadı. #353: `geri` verilirse yeni sekmedeki kişi sayfasının "geri dön"ü
  // çağıran sayfanın tam adresine gider (kisiSayfasiUrl `geri`yi kendisi kodlar); yoksa varsayılan Kişi Listesi.
  const kisiUrl = (hedef) => kisiSayfasiUrl({ personId, fuarId, geri, geriEtiket, ...hedef });
  const profilUrl = (id) => `/Management/Firmalar/Profil/${id}?tab=belgeler&fuarId=${fuarId}`;
  const profil = profilUrl(firmaId);
  // #400: firma evrakı HİÇBİR firmada tam değilse bağlantı, hangi firmanın profiline gidileceğini sorar (üstte firma seçici yok).
  const secenekler = firmaSecenekleri?.length > 1
    ? firmaSecenekleri.map((f) => ({ firmaId: f.firmaId, ad: firmaAdi(f), href: profilUrl(f.firmaId) }))
    : null;
  if (code === "KVKK" || code === "KIMLIK_DOGRULAMA") {
    // #481: Kontroller sekmesinde ilgili kart (KVKK ya da Kimlik doğrulama) kırmızı çerçevelenir.
    const vurgu = code === "KVKK" ? "kvkk" : "kimlik";
    return { etiket: i18n.t("components:kartUygunsuzluklari.kontroller"), tab: "kimlik", vurgu, href: kisiUrl({ docKey: vurgu }) };
  }
  // #514: Kimlik Fotoğrafı (file_type 90) İSG Belgeleri'nde kutu ya da onay düğmesi taşımaz; kimlik belgesi Kontroller'de
  // "Kimliği doğrula" penceresinde onaylanır.
  if (code === "BELGE:kimlikFotografi") {
    return { etiket: i18n.t("components:kartUygunsuzluklari.kontroller"), tab: "kimlik", vurgu: "kimlik", href: kisiUrl({ docKey: "kimlik" }) };
  }
  if (code?.startsWith("BELGE:")) {
    const belge = code.slice(6);
    // #363: Mesleki Eğitim'in yüklemesi İSG Belgeleri › Meslek alt sekmesinde (kisiSayfasiUrl `docKey`ten çözer).
    if (belge === "meslekiEgitim") {
      return { etiket: i18n.t("components:kartUygunsuzluklari.meslek"), tab: "belgeler", alt: "meslek", belge, href: kisiUrl({ docKey: belge }) };
    }
    return { etiket: i18n.t("components:kartUygunsuzluklari.isgBelgeleri"), tab: "belgeler", belge, href: kisiUrl({ docKey: belge }) };
  }
  if (code === "FIRMA_BELGELERI" || code === "FIRMA_GENEL_BELGELERI" || code === "VERGI_NO") {
    return { etiket: i18n.t("components:kartUygunsuzluklari.firmaProfili"), href: profil, secenekler };
  }
  return null;
}

function HedefBaglantisi({ hedef, yeniSekme, onSayfaIci }) {
  const icerik = <>{hedef.etiket}<ArrowUpRight size={12} /></>;
  if (hedef.secenekler) {
    return (
      <UncontrolledDropdown tag="span" className="d-inline-block">
        <DropdownToggle tag="button" type="button" className="btn btn-link btn-sm p-0 align-baseline small">
          {hedef.etiket}<ChevronDown size={12} />
        </DropdownToggle>
        <DropdownMenu>
          {hedef.secenekler.map((s) => (yeniSekme
            ? <DropdownItem key={s.firmaId} tag="a" href={s.href} target="_blank" rel="noopener noreferrer">{s.ad}</DropdownItem>
            : <DropdownItem key={s.firmaId} tag={Link} to={s.href}>{s.ad}</DropdownItem>))}
        </DropdownMenu>
      </UncontrolledDropdown>
    );
  }
  if (onSayfaIci && hedef.tab) {
    return (
      <button
        type="button" className="btn btn-link btn-sm p-0 align-baseline small"
        onClick={() => onSayfaIci({ tab: hedef.tab, alt: hedef.alt, belge: hedef.belge, vurgu: hedef.vurgu })}
      >
        {icerik}
      </button>
    );
  }
  if (yeniSekme) {
    return <a href={hedef.href} target="_blank" rel="noopener noreferrer" className="small">{icerik}</a>;
  }
  return <Link to={hedef.href} className="small">{icerik}</Link>;
}

/** Belge dışı kodların ikonu (#350); belgeler `belgeIkonu`ndan. Kartı engelleyen her satır varsayılan kırmızıdır. */
const KOD_IKONU = {
  KVKK: ShieldCheck,
  KIMLIK_DOGRULAMA: IdCard,
  FIRMA_BELGELERI: ClipboardCheck,
  FIRMA_GENEL_BELGELERI: Building2,
  VERGI_NO: Receipt,
  YASAKLI: Ban,
  FIRMA_REDDETTI: Ban,
};

/** SGK / İSG / Sağlık / Mesleki Eğitim Kişi Listesi'ndeki karelerle aynı ikon; öteki belge anahtarları genel dosya ikonu. */
function belgeIkonu(key) {
  return PERSONEL_BELGE_META.find((m) => m.key === key)?.icon ?? FileText;
}

/**
 * Bir belge engelinin durumu — `kisi.documents[key]`dan (#350). Dönen `renk` kare rengi, `neden` Reddedildi'nin açıklaması.
 * Backend yaş kuralında (`yasiGecmis`) belgenin kendi kararı onaylı/bekliyor kalır ama belge EKSİK sayılır; aynı dosyayı
 * yeniden onaylamak çözmez, yeni dosya gerekir. Bu yüzden Reddedildi dışında yaş kuralı önce okunur.
 */
function belgeDurumu(key, kisi) {
  return belgeDurumuOf(kisi?.documents?.[key]);
}

/** `belgeDurumu`nun tek belge halı (#372: bir mesleğin sertifika yuvasının `belge`si de aynı sözlükle okunur). */
function belgeDurumuOf(d) {
  if (!d || d.status == null) return { durum: "Eksik", renk: NEUTRAL_GRAY };
  if (d.status === 2) return { durum: "Reddedildi", renk: STATUS_HEX.danger, neden: d.rejectReason || null };
  if (d.yasiGecmis) return { durum: i18n.t("components:kartUygunsuzluklari.fuarIcinCokEski"), renk: STATUS_HEX.danger };
  if (d.status === 0) {
    return d.expired
      ? { durum: i18n.t("components:kartUygunsuzluklari.suresiDolmus"), renk: STATUS_HEX.danger }
      : { durum: "Onay bekliyor", renk: STATUS_HEX.warning };
  }
  return { durum: i18n.t("components:kartUygunsuzluklari.gecerliDegil"), renk: STATUS_HEX.danger };
}

/**
 * ⚠️ #466 (backend #464): backend artık `BELGE:meslekiEgitim`i engel listesine KOYMAZ (kart "Çalışan" olarak basılır); bu yol yalnızca
 * eski bir yanıt için duruyor. Eksik meslek sertifikası şimdi `MeslekBilgisi` bilgi satırıdır.
 *
 * Mesleki Eğitim engelinin altına: kişi bu fuarda 2+ meslek taşıyorsa HANGİ mesleğin sertifikası eksik (backend #371: kod hâlâ
 * `BELGE:meslekiEgitim`, hangisi `meslekiEgitimler[]`de `istenen && !sayilir` olandır). Tek meslekte ya da hepsi tamamsa null.
 */
function eksikMeslekMetni(kisi) {
  const yuvalar = kisi?.meslekiEgitimler;
  if (!Array.isArray(yuvalar) || yuvalar.length < 2) return null;
  const eksik = yuvalar.filter((y) => y.istenen && !y.sayilir);
  if (eksik.length === 0) return null;
  return eksik.map((y) => `${y.meslekAdi}: ${belgeDurumuOf(y.belge).durum}`).join(" · ");
}

/** Bir engelin satırı için { Icon, renk, metin, neden, ek }. */
function satirBilgisi(code, kisi, firmaGrubu) {
  if (code?.startsWith("BELGE:")) {
    const key = code.slice(6);
    const Icon = belgeIkonu(key);
    // `kisi` yoksa durum bilinmez: eski metin, ama yine ikonlu ve (engel olduğu için) kırmızı.
    if (!kisi) return { Icon, renk: STATUS_HEX.danger, metin: uygunsuzlukLabel(code, firmaGrubu) };
    const { durum, renk, neden } = belgeDurumu(key, kisi);
    return { Icon, renk, metin: `${kisiBelgeLabel(key)}: ${durum}`, neden, ek: key === "meslekiEgitim" ? eksikMeslekMetni(kisi) : null };
  }
  return { Icon: KOD_IKONU[code] ?? FileText, renk: STATUS_HEX.danger, metin: uygunsuzlukLabel(code, firmaGrubu) };
}

export function IkonKaresi({ Icon, renk, baslik }) {
  return (
    <span
      className="d-inline-flex align-items-center justify-content-center rounded-2 flex-shrink-0"
      style={{ width: 28, height: 28, background: renk }} title={baslik}
    >
      <Icon size={15} color="#fff" />
    </span>
  );
}

/**
 * #466: bir mesleğin `kartMeslekleri[].eksikler` kodunun kısa adı ("Mesleki Eğitim", "personel onayı"). Bir engel cümlesi değil, yalnızca
 * "<meslek>: <eksik> eksik" satırının ve meslek kartının "Eksik: ..." satırının parçası.
 */
export function meslekEksikAdi(kod) {
  if (kod === "MESLEK_ONAYI") return i18n.t("components:kartUygunsuzluklari.personelOnayi");
  return kod?.startsWith("BELGE:") ? kisiBelgeLabel(kod.slice(6)) : uygunsuzlukLabel(kod);
}

/**
 * #466 (backend #464): Mesleki Eğitim kartı ENGELLEMEZ — kart "Çalışan" olarak basılır. Backend artık `BELGE:meslekiEgitim`i engel
 * listesine koymaz; ama kişinin tuttuğu bir meslek (kendi belge setiyle) kart için eksikse bu bir BİLGİ satırıdır: gri ikon, engel
 * değil. Metin backend'in `kartMeslekleri[].eksikler`inden geldiği gibi; burada karar türetilmez.
 */
export function MeslekBilgisi({ kisi, kodlar }) {
  const { t } = useTranslation("components");
  const bilgiler = meslekBilgiEksikleri(kisi, kodlar);
  if (bilgiler.length === 0) return null;
  // Kart başka bir engelle hiç çıkmıyorsa "Çalışan olarak basılır" demek yanlış olur.
  const calisanBasilir = kodlar.length === 0 && kisi?.kartCalisanBasilabilir !== false;
  return (
    <ul className="list-unstyled mb-0 mt-2">
      {bilgiler.map((b) => (
        <li key={b.meslekAdi} className="d-flex align-items-center gap-2 mb-1">
          <IkonKaresi Icon={Info} renk={NEUTRAL_GRAY} baslik={b.meslekAdi} />
          <span className="text-muted">
            {t(calisanBasilir ? "kartUygunsuzluklari.meslekBilgisiCalisan" : "kartUygunsuzluklari.meslekBilgisi", {
              meslek: b.meslekAdi, eksik: b.kodlar.map(meslekEksikAdi).join(", "),
            })}
          </span>
        </li>
      ))}
    </ul>
  );
}

export default function KartUygunsuzluklari({
  uygunsuzluklar = [], vergiNoYok = false, firmaId, personId, fuarId, yeniSekme = false, onSayfaIci, firmaGrubu = null,
  kisi = null, geri = null, geriEtiket = null, firmaSecenekleri = null,
}) {
  const { t } = useTranslation("components");
  const kodlar = vergiNoYok ? [...uygunsuzluklar, "VERGI_NO"] : uygunsuzluklar;
  if (kodlar.length === 0) {
    return (
      <>
        <div className="d-flex align-items-center gap-2 text-success">
          <CheckCircle2 size={18} />
          <span>{t("kartUygunsuzluklari.girisKartiIcinTumKosullar")}</span>
        </div>
        <MeslekBilgisi kisi={kisi} kodlar={kodlar} />
      </>
    );
  }
  return (
    <>
    <ul className="list-unstyled mb-0">
      {kodlar.map((code) => {
        const hedef = hedefOf(code, { firmaId, personId, fuarId, geri, geriEtiket, firmaSecenekleri });
        const { Icon, renk, metin, neden, ek } = satirBilgisi(code, kisi, firmaGrubu);
        return (
          <li key={code} className="d-flex align-items-center gap-2 mb-2">
            <IkonKaresi Icon={Icon} renk={renk} baslik={metin} />
            <div style={{ minWidth: 0 }}>
              <div>
                {metin}
                {hedef && (
                  <>
                    {" "}
                    <HedefBaglantisi hedef={hedef} yeniSekme={yeniSekme} onSayfaIci={onSayfaIci} />
                  </>
                )}
              </div>
              {neden && <div className="text-muted small">{neden}</div>}
              {ek && <div className="text-muted small">{ek}</div>}
            </div>
          </li>
        );
      })}
    </ul>
    <MeslekBilgisi kisi={kisi} kodlar={kodlar} />
    </>
  );
}
